import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Leaf, Heartbeat, Sun, Wallet, ChatsCircle } from "@phosphor-icons/react";

/**
 * Business-Line switcher — one-tap navigation between the app's arms.
 * Each arm has a distinct colour so operators always know which
 * command centre they're on. Colour system:
 *   Hygiene   → teal   (#0F766E / bg #CCFBF1)
 *   Health    → red    (#B91C1C / bg #FEE2E2)
 *   Happiness → yellow (#B45309 / bg #FEF3C7)
 *   Wallet    → indigo (#4338CA / bg #E0E7FF)
 *   CRM       → violet (#6D28D9 / bg #EDE9FE)
 *
 * Rendered inside the Topbar so it's visible on every admin page.
 */
const ARMS = [
  { key: "hygiene",   label: "Hygiene",   to: "/dashboard",        icon: Leaf,        color: "#0F766E", bg: "#CCFBF1" },
  { key: "health",    label: "Health",    to: "/health-services",  icon: Heartbeat,   color: "#B91C1C", bg: "#FEE2E2" },
  { key: "happiness", label: "Happiness", to: "/happiness",        icon: Sun,         color: "#B45309", bg: "#FEF3C7", disabled: true },
  { key: "wallet",    label: "Wallet",    to: "/finance/wallet-txns", icon: Wallet,   color: "#4338CA", bg: "#E0E7FF" },
  { key: "crm",       label: "CRM",       to: "/crm/chats",        icon: ChatsCircle, color: "#6D28D9", bg: "#EDE9FE" },
];

function detectActive(pathname) {
  if (pathname.startsWith("/health-services") || pathname.startsWith("/emdispatch") || pathname.startsWith("/health")) return "health";
  if (pathname.startsWith("/happiness")) return "happiness";
  if (pathname.startsWith("/finance/wallet") || pathname.startsWith("/customers/wallet")) return "wallet";
  if (pathname.startsWith("/crm")) return "crm";
  return "hygiene";
}

export default function ArmSwitcher() {
  const location = useLocation();
  const navigate = useNavigate();
  const active = detectActive(location.pathname);

  return (
    <div
      data-testid="arm-switcher"
      className="hidden md:flex items-center gap-1 px-1.5 py-1 rounded-full bg-[#F5F3EE] border border-[#E5E2D9]"
    >
      {ARMS.map((arm) => {
        const Icon = arm.icon;
        const isActive = active === arm.key;
        const disabled = arm.disabled;
        return (
          <button
            key={arm.key}
            data-testid={`arm-chip-${arm.key}`}
            disabled={disabled}
            onClick={() => !disabled && navigate(arm.to)}
            title={disabled ? `${arm.label} · coming soon` : arm.label}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-medium transition-all ${
              disabled
                ? "opacity-40 cursor-not-allowed text-[#8A8780]"
                : isActive
                ? "shadow-sm"
                : "hover:bg-white text-[#5C5A55]"
            }`}
            style={
              isActive && !disabled
                ? { backgroundColor: arm.bg, color: arm.color }
                : undefined
            }
          >
            <Icon size={14} weight={isActive ? "fill" : "regular"} />
            <span>{arm.label}</span>
          </button>
        );
      })}
    </div>
  );
}
